import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useMeta from '../hooks/useMeta';

const objectiveLabels = {
  LINK_CLICKS: 'Cliques no Link',
  CONVERSIONS: 'Conversões',
  LEAD_GENERATION: 'Geração de Cadastros',
  BRAND_AWARENESS: 'Reconhecimento da Marca',
  REACH: 'Alcance',
  VIDEO_VIEWS: 'Visualizações de Vídeo',
  MESSAGES: 'Mensagens',
  APP_INSTALLS: 'Instalações do App',
};

const statusColors = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  PAUSED: 'bg-yellow-100 text-yellow-800',
  ARCHIVED: 'bg-slate-100 text-slate-500',
  DELETED: 'bg-red-100 text-red-700',
};

const statusTranslation = {
  ACTIVE: 'ATIVA',
  PAUSED: 'PAUSADA',
  ARCHIVED: 'ARQUIVADA',
  DELETED: 'EXCLUÍDA',
};

const Campaigns = () => {
  const { accountId } = useParams();
  const { campaigns, loading, error, fetchCampaigns } = useMeta();
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (accountId) {
      fetchCampaigns(accountId);
    }
  }, [accountId]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await fetchCampaigns(accountId);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="animate-fade-in pb-16">
      <div className="mb-12 flex justify-between items-end">
        <div>
          <h1 className="font-headline font-extrabold text-4xl tracking-tight text-on-surface mb-2">Campanhas</h1>
          <p className="text-secondary font-body">
            Conta: <span className="font-mono text-sm">act_{accountId}</span>
          </p>
        </div>
        <div className="flex gap-4">
           <Link to="/accounts" className="px-6 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all text-on-surface flex items-center gap-1">
             <span className="material-symbols-outlined text-sm">arrow_back</span> Voltar
           </Link>
           <button
             onClick={handleSync}
             disabled={syncing || loading}
             className="primary-pill-gradient text-white px-6 py-2 rounded-full text-sm font-manrope font-semibold hover:opacity-90 transition-all font-headline flex items-center gap-2"
           >
             <span className="material-symbols-outlined text-sm">sync</span> {syncing ? 'Sincronizando...' : 'Sincronizar Campanhas'}
           </button>
        </div>
      </div>

      <div className="bg-surface-container-low rounded-xl p-10">
        <h2 className="font-headline font-bold text-2xl text-on-surface mb-8">
          Lista de Campanhas{campaigns.length > 0 && (
            <span className="text-sm font-normal text-slate-400 ml-2">({campaigns.length})</span>
          )}
        </h2>

        {error && (
          <div className="rounded-xl bg-error-container border border-error/20 p-6 mb-8 flex items-start gap-4">
             <span className="material-symbols-outlined text-error mt-0.5">error</span>
             <div>
                <p className="text-sm font-bold text-on-error-container mb-1">{error}</p>
             </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse h-20 bg-surface-container-highest/20 rounded-xl" />
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-surface-container-lowest rounded-xl p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-surface-container flex items-center justify-center mx-auto mb-6 text-slate-400">
               <span className="material-symbols-outlined text-3xl">campaign</span>
            </div>
            <p className="font-headline font-bold text-xl text-slate-800">Nenhuma campanha encontrada</p>
            <p className="text-sm mt-2 text-slate-500">Sincronize para buscar as campanhas do Meta.</p>
            <button onClick={handleSync} className="btn primary-pill-gradient text-white mt-6 rounded-full px-6 py-2 font-bold hover:opacity-90">
              Sincronizar Agora
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Objetivo</th>
                  <th>Status</th>
                  <th>Início</th>
                  <th>Fim</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="bg-surface-container-lowest group hover:shadow-md transition-shadow">
                    <td className="px-6 py-6 rounded-l-xl">
                       <p className="font-headline font-bold text-sm text-on-surface">{campaign.name}</p>
                       <p className="text-[10px] text-secondary font-mono mt-1">ID: {campaign.id}</p>
                    </td>
                    <td className="px-6 py-6 text-sm text-secondary font-medium">
                      {objectiveLabels[campaign.objective] || campaign.objective || '—'}
                    </td>
                    <td className="px-6 py-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[campaign.status] || 'bg-slate-100 text-slate-600'}`}>
                        {statusTranslation[campaign.status] || campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-6 text-sm text-secondary font-medium">
                      {campaign.start_time ? new Date(campaign.start_time).toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td className="px-6 py-6 text-sm text-secondary font-medium rounded-r-xl">
                      {campaign.stop_time ? new Date(campaign.stop_time).toLocaleDateString('pt-BR') : 'Contínua'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Campaigns;
